import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { currencyInput } from "@/shared/utils/currencyInput";
import type { EstateItem } from "@/shared/types/estateTypes";

export interface OtherLoanFormValues {
  loanType: string;
  lenderName: string;
  balance: string;
  monthlyPayment: string;
  statementFile: File | null;
}

export const EMPTY_OTHER_LOAN_FORM: OtherLoanFormValues = {
  loanType: "",
  lenderName: "",
  balance: "",
  monthlyPayment: "",
  statementFile: null,
};

export interface OtherLoanFieldErrors {
  loanType?: string;
}

export function validateOtherLoanFields(form: OtherLoanFormValues): OtherLoanFieldErrors {
  if (!form.loanType) return { loanType: "Please select a loan type" };
  return {};
}

export function otherLoanToItemFields(form: OtherLoanFormValues) {
  const loanType = form.loanType;
  const lenderName = form.lenderName.trim();

  return {
    item_name: loanType,
    item_type: loanType,
    balance: parseFloat(form.balance) || 0,
    metadata: {
      lender_name: lenderName || undefined,
      monthly_payment: parseFloat(form.monthlyPayment) || undefined,
    },
  };
}

export function parseItemToForm(item: EstateItem): OtherLoanFormValues {
  const meta = (item.metadata ?? {}) as Record<string, unknown>;
  return {
    loanType: item.item_type ?? "",
    lenderName: typeof meta.lender_name === "string" ? meta.lender_name : "",
    balance: item.balance ? String(item.balance) : "",
    monthlyPayment: typeof meta.monthly_payment === "number" ? String(meta.monthly_payment) : "",
    statementFile: null,
  };
}

const LOAN_TYPES = [
  "Personal loan",
  "Auto loan",
  "Student loan",
  "Home equity line of credit (HELOC)",
  "Medical debt",
  "Business loan",
  "Other",
] as const;

interface OtherLoanFormProps {
  form: OtherLoanFormValues;
  onChange: (form: OtherLoanFormValues) => void;
  fieldErrors?: OtherLoanFieldErrors;
  existingFileName?: string | null;
  onExistingFileRemove?: () => void;
}

export const OtherLoanForm: FC<OtherLoanFormProps> = ({
  form,
  onChange,
  fieldErrors,
  existingFileName,
  onExistingFileRemove,
}) => {
  const balanceInput = currencyInput(form.balance, (val) => onChange({ ...form, balance: val }));
  const monthlyPaymentInput = currencyInput(form.monthlyPayment, (val) =>
    onChange({ ...form, monthlyPayment: val }),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">Loan type</Label>
        <Select value={form.loanType} onValueChange={(val) => onChange({ ...form, loanType: val })}>
          <SelectTrigger className={fieldErrors?.loanType ? "border-error" : ""}>
            <SelectValue placeholder="Select the loan type" />
          </SelectTrigger>
          <SelectContent>
            {LOAN_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors?.loanType} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Lender name <span className="text-tertiary">(optional)</span>
        </Label>
        <Input
          value={form.lenderName}
          onChange={(e) => onChange({ ...form, lenderName: e.target.value })}
          placeholder="Lender name"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Approximate balance <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={balanceInput.displayValue}
            onChange={balanceInput.handleChange}
            placeholder="0.00"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Monthly payment <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={monthlyPaymentInput.displayValue}
            onChange={monthlyPaymentInput.handleChange}
            placeholder="0.00"
          />
        </div>
      </div>

      <UploadZone
        label="Do you have loan documents or statements?"
        labelSuffix="(optional)"
        file={form.statementFile}
        existingFileName={existingFileName}
        onFileSelect={(file) => onChange({ ...form, statementFile: file })}
        onFileRemove={() => {
          onChange({ ...form, statementFile: null });
          onExistingFileRemove?.();
        }}
      />
    </div>
  );
};
